/**
 * ResponseTrendChart — Multi-line response time trend (one line per area type, 4 weeks).
 * Each area record has: area_type_name, week1, week2, week3, week4
 */

import { useMemo } from 'react';
import LineTrendingChart from '../../../../components/charts/LineTrendingChart.jsx';
import { useChartSeries } from '../../../../components/charts/chartPalette.js';
import { formatDateNoYear, getToday } from '../../../../libs.jsx';
import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';

const TREND_TOOLTIP = [
    'Displays the average response time in minutes for each 7-day period',
    'over the previous 4 weeks, broken down by care type. Click a care type',
    'in the legend to show or hide it on the graph.',
];

/* ── TrendInfoTip — ⓘ icon with hover popover ── */
const TrendInfoTip = () => (
    <HeaderInfoTip title="About This Chart" body={TREND_TOOLTIP.join(' ')} />
);

const ResponseTrendChart = ({ areaData }) => {
    const series = useChartSeries();
    const chartProps = useMemo(() => {
        if (!Array.isArray(areaData) || areaData.length === 0) return null;

        // Week labels (oldest to newest, matching V1 order: week4, week3, week2, week1)
        const labels = [
            `${formatDateNoYear(getToday(-28))}–${formatDateNoYear(getToday(-21))}`,
            `${formatDateNoYear(getToday(-21))}–${formatDateNoYear(getToday(-14))}`,
            `${formatDateNoYear(getToday(-14))}–${formatDateNoYear(getToday(-8))}`,
            `${formatDateNoYear(getToday(-8))}–${formatDateNoYear(getToday(-1))}`,
        ];

        // Each area type becomes a line: [week4, week3, week2, week1]
        const lineData = areaData.map((at) => [at.week4, at.week3, at.week2, at.week1]);
        const lineLabels = areaData.map((at) => at.area_type_name || '');
        const lineColors = areaData.map((_, i) => series[i % series.length]);

        return { labels, lineData, lineLabels, lineColors, lines: areaData.length };
    }, [areaData, series]);

    if (!chartProps) {
        return (
            <div className="md-section">
                <h3 className="md-section__title">Response Time Trends<TrendInfoTip /></h3>
                <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>No trend data available</div>
            </div>
        );
    }

    return (
        <div className="md-section">
            <h3 className="md-section__title" style={{ marginBottom: 'var(--space-4)' }}>Response Time Trends<TrendInfoTip /></h3>
            <div className="md-chart-container">
                <LineTrendingChart
                    style={{ maxHeight: '350px' }}
                    x_axis={true}
                    x_axis_title="Weeks"
                    line_axis={true}
                    line_axis_title="Response Time (min)"
                    lines={chartProps.lines}
                    line_color={chartProps.lineColors}
                    line_labels={chartProps.lineLabels}
                    line_data={chartProps.lineData}
                    forceAspect={true}
                    labels={chartProps.labels}
                />
            </div>
        </div>
    );
};

export default ResponseTrendChart;
